"use strict";

// eslint-disable-next-line no-constant-binary-expression

var EventEmitter = require("events-light");
var SubscriptionTracker = require("listener-tracker");
var inherit = require("raptor-util/inherit");
var componentsUtil = require("@internal/components-util");
var componentLookup = componentsUtil._l_;
var destroyNodeRecursive = componentsUtil._R_;
var defaultCreateOut = require("../createOut");
var domInsert = require("../dom-insert");
var RenderResult = require("../RenderResult");
var morphdom = require("../vdom/morphdom");
var getComponentsContext =
require("./ComponentsContext").S_;
var domData = require("./dom-data");
var eventDelegation = require("./event-delegation");
var updateManager = require("./update-manager");
var componentsByDOMNode = domData._p_;
var keyedElementsByComponentId = domData._o_;
var CONTEXT_KEY = "__subtree_context__";

var hasOwnProperty = Object.prototype.hasOwnProperty;
var slice = Array.prototype.slice;

var COMPONENT_SUBSCRIBE_TO_OPTIONS;
var NON_COMPONENT_SUBSCRIBE_TO_OPTIONS = {
  addDestroyListener: false
};

var emit = EventEmitter.prototype.emit;
var ELEMENT_NODE = 1;

function removeListener(removeEventListenerHandle) {
  removeEventListenerHandle();
}

function walkFragments(fragment) {
  var node;

  while (fragment) {
    node = fragment.firstChild;

    if (!node) {
      break;
    }

    fragment = node.fragment;
  }

  return node;
}

function handleCustomEventWithMethodListener(
component,
targetMethodName,
args,
extraArgs)
{
  // Remove the "eventType" argument
  args.push(component);

  if (extraArgs) {
    args = extraArgs.concat(args);
  }

  var targetComponent = componentLookup[component.X_];
  var targetMethod =
  typeof targetMethodName === "function" ?
  targetMethodName :
  targetComponent[targetMethodName];
  if (!targetMethod) {
    throw Error("Method not found: " + targetMethodName);
  }

  targetMethod.apply(targetComponent, args);
}

function resolveKeyHelper(key, index) {
  return index ? key + "_" + index : key;
}

function resolveComponentIdHelper(component, key, index) {
  return component.id + "-" + resolveKeyHelper(key, index);
}

/**
 * This method is used to process "update_<stateName>" handler functions.
 * If all of the modified state properties have a user provided update handler
 * then a rerender will be bypassed and, instead, the DOM will be updated
 * looping over and invoking the custom update handlers.
 * @return {boolean} Returns true if if the DOM was updated. False, otherwise.
 */
function processUpdateHandlers(component, stateChanges, oldState) {
  var handlerMethod;
  var handlers;

  for (var propName in stateChanges) {
    if (hasOwnProperty.call(stateChanges, propName)) {
      var handlerMethodName = "update_" + propName;

      handlerMethod = component[handlerMethodName];
      if (handlerMethod) {
        (handlers || (handlers = [])).push([propName, handlerMethod]);
      } else {
        // This state change does not have a state handler so return false
        // to force a rerender
        return;
      }
    }
  }

  // If we got here then all of the changed state properties have
  // an update handler or there are no state properties that actually
  // changed.
  if (handlers) {
    // Otherwise, there are handlers for all of the changed properties
    // so apply the updates using those handlers

    handlers.forEach(function (handler) {
      var propertyName = handler[0];
      handlerMethod = handler[1];

      var newValue = stateChanges[propertyName];
      var oldValue = oldState[propertyName];
      handlerMethod.call(component, newValue, oldValue);
    });

    component._y_();
    component._t_();
  }

  return true;
}

function checkInputChanged(existingComponent, oldInput, newInput) {
  if (oldInput != newInput) {
    if (oldInput == null || newInput == null) {
      return true;
    }

    var oldKeys = Object.keys(oldInput);
    var newKeys = Object.keys(newInput);
    var len = oldKeys.length;
    if (len !== newKeys.length) {
      return true;
    }

    for (var i = len; i--;) {
      var key = oldKeys[i];
      if (!(key in newInput && oldInput[key] === newInput[key])) {
        return true;
      }
    }
  }

  return false;
}

var componentProto;

/**
 * Base component type.
 *
 * NOTE: Any methods that are prefixed with an underscore should be considered private!
 */
function Component(id) {
  EventEmitter.call(this);
  this.id = id;
  this.z_ = null;
  this._E_ = null;
  this.ai_ = null;
  this._w_ = null;
  this.Y_ = null; // Used to keep track of bubbling DOM events for components rendered on the server
  this.W_ = null;
  this.X_ = null;
  this.aj_ = null;
  this.O_ = undefined;
  this._x_ = false;
  this.ak_ = undefined;
  this.I_ = false;
  this._b_ = false;
  this.al_ = false;
  this.am_ = false;
  this.B_ = undefined;

  var ssrKeyedElements = keyedElementsByComponentId[id];

  if (ssrKeyedElements) {
    this.K_ = ssrKeyedElements;
    delete keyedElementsByComponentId[id];
  } else {
    this.K_ = {};
  }
}

Component.prototype = componentProto = {
  x_: true,

  subscribeTo: function (target) {
    if (!target) {
      throw TypeError();
    }

    var subscriptions =
    this.ai_ || (
    this.ai_ = new SubscriptionTracker());

    var subscribeToOptions = target.x_ ?
    COMPONENT_SUBSCRIBE_TO_OPTIONS :
    NON_COMPONENT_SUBSCRIBE_TO_OPTIONS;

    return subscriptions.subscribeTo(target, subscribeToOptions);
  },

  emit: function (eventType) {
    var customEvents = this.W_;
    var target;

    if (customEvents && (target = customEvents[eventType])) {
      var targetMethodName = target[0];
      var isOnce = target[1];
      var extraArgs = target[2];
      var args = slice.call(arguments, 1);

      handleCustomEventWithMethodListener(
        this,
        targetMethodName,
        args,
        extraArgs
      );

      if (isOnce) {
        delete customEvents[eventType];
      }
    }

    return emit.apply(this, arguments);
  },
  getElId: function (key, index) {
    if (!key) {
      return this.id;
    }
    return resolveComponentIdHelper(this, key, index);
  },
  getEl: function (key, index) {
    if (key) {
      var resolvedKey = resolveKeyHelper(key, index);
      var keyedElement = this.K_["@" + resolvedKey];
      if (keyedElement && keyedElement.nodeType === 12 /** FRAGMENT_NODE */) {
        // eslint-disable-next-line no-constant-condition






        return walkFragments(keyedElement);
      }

      return keyedElement;
    } else {
      return this.el;
    }
  },
  getEls: function (key) {
    key = key + "[]";

    var els = [];
    var i = 0;
    var el;
    while (el = this.getEl(key, i)) {
      els.push(el);
      i++;
    }
    return els;
  },
  getComponent: function (key, index) {
    var rootNode = this.K_["@" + resolveKeyHelper(key, index)];
    if (/\[\]$/.test(key)) {
      // eslint-disable-next-line no-constant-condition





      rootNode = rootNode && rootNode[Object.keys(rootNode)[0]];
    }
    return rootNode && componentsByDOMNode.get(rootNode);
  },
  getComponents: function (key) {
    var lookup = this.K_["@" + key + "[]"];
    return lookup ?
    Object.keys(lookup).
    map(function (key) {
      return componentsByDOMNode.get(lookup[key]);
    }).
    filter(Boolean) :
    [];
  },
  destroy: function () {
    if (this.I_) {
      return;
    }

    var root = this._E_;

    this._N_();

    var nodes = root.nodes;

    nodes.forEach(function (node) {
      destroyNodeRecursive(node);

      if (eventDelegation.an_(node) !== false) {
        node.parentNode.removeChild(node);
      }
    });

    root.detached = true;

    delete componentLookup[this.id];
    this.K_ = {};
  },

  _N_: function () {
    if (this.I_) {
      return;
    }

    this.ao_();
    this.I_ = true;

    componentsByDOMNode.set(this._E_, undefined);

    this._E_ = null;

    // Unsubscribe from all DOM events
    this._v_();

    var subscriptions = this.ai_;
    if (subscriptions) {
      subscriptions.removeAllListeners();
      this.ai_ = null;
    }
  },

  isDestroyed: function () {
    return this.I_;
  },
  get state() {
    return this.z_;
  },
  set state(newState) {
    var state = this.z_;
    if (!state && !newState) {
      return;
    }

    if (!state) {
      state = this.z_ = new this.P_(this);
    }

    state.ap_(newState || {});

    if (state.al_) {
      this.aq_();
    }

    if (!newState) {
      this.z_ = null;
    }
  },
  setState: function (name, value) {
    var state = this.z_;

    if (!state) {
      state = this.z_ = new this.P_(this);
    }
    if (typeof name == "object") {
      // Merge in the new state with the old state
      var newState = name;
      for (var k in newState) {
        if (hasOwnProperty.call(newState, k)) {
          state.ar_(k, newState[k], true /* ensure:true */);
        }
      }
    } else {
      state.ar_(name, value, true /* ensure:true */);
    }
  },

  setStateDirty: function (name, value) {
    var state = this.z_;

    if (arguments.length == 1) {
      value = state[name];
    }

    state.ar_(
      name,
      value,
      true /* ensure:true */,
      true /* forceDirty:true */
    );
  },

  replaceState: function (newState) {
    this.z_.ap_(newState);
  },

  get input() {
    return this.O_;
  },
  set input(newInput) {
    if (this.am_) {
      this.O_ = newInput;
    } else {
      this._i_(newInput);
    }
  },

  _i_: function (newInput, onInput, out) {
    onInput = onInput || this.onInput;
    var updatedInput;

    var oldInput = this.O_;
    this.O_ = undefined;
    this.as_ = out && out[CONTEXT_KEY] || this.as_;

    if (onInput) {
      // We need to set a flag to preview `this.input = foo` inside
      // onInput causing infinite recursion
      this.am_ = true;
      updatedInput = onInput.call(this, newInput || {}, out);
      this.am_ = false;
    }

    newInput = this.aj_ = updatedInput || newInput;

    if (this.al_ = checkInputChanged(this, oldInput, newInput)) {
      this.aq_();
    }

    if (this.O_ === undefined) {
      this.O_ = newInput;
      if (newInput && newInput.$global) {
        this.ak_ = newInput.$global;
      }
    }

    return newInput;
  },

  forceUpdate: function () {
    this.al_ = true;
    this.aq_();
  },

  aq_: function () {
    if (!this._b_) {
      this._b_ = true;
      updateManager.at_(this);
    }
  },

  update: function () {
    if (this.I_ === true || this.au_ === false) {
      return;
    }

    var input = this.O_;
    var state = this.z_;

    if (this.al_ === false && state !== null && state.al_ === true) {
      if (processUpdateHandlers(this, state.av_, state.aw_, state)) {
        state.al_ = false;
      }
    }

    if (this.au_ === true) {
      // The UI component is still dirty after process state handlers
      // then we should rerender

      if (this.shouldUpdate(input, state) !== false) {
        this.ax_();
      }
    }

    this._t_();
  },

  get au_() {
    return (
      this.al_ === true ||
      this.z_ !== null && this.z_.al_ === true);

  },

  _t_: function () {
    this.al_ = false;
    this._b_ = false;
    this.aj_ = null;
    var state = this.z_;
    if (state) {
      state._t_();
    }
  },

  shouldUpdate: function () {
    return true;
  },

  ax_: function () {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    var self = this;
    var renderer = self.Q_;

    if (!renderer) {
      throw TypeError();
    }

    var input = this.aj_ || this.O_;

    updateManager.ay_(function () {
      self._F_(input, false).afterInsert(self.B_);
    });

    this._t_();
  },

  _F_: function (input, isHydrate) {
    var host = this.B_;
    var globalData = this.ak_;
    var rootNode = this._E_;
    var renderer = this.Q_;
    var createOut = renderer.createOut || defaultCreateOut;
    var out = createOut(globalData);
    out.sync();
    out.B_ = this.B_;
    out[CONTEXT_KEY] = this.as_;

    var componentsContext = getComponentsContext(out);
    var globalComponentsContext = componentsContext.p_;
    globalComponentsContext.az_ = this;
    globalComponentsContext._Z_ = isHydrate;

    renderer(input, out);

    var result = new RenderResult(out);

    var targetNode = out.ah_().aA_;

    morphdom(rootNode, targetNode, host, componentsContext);

    return result;
  },

  aB_: function () {
    var root = this._E_;
    root.remove();
    return root;
  },

  _v_: function () {
    var eventListenerHandles = this._w_;
    if (eventListenerHandles) {
      eventListenerHandles.forEach(removeListener);
      this._w_ = null;
    }
  },

  get aC_() {
    var state = this.z_;
    return state && state.A_;
  },

  aD_: function (customEvents, scope) {
    var finalCustomEvents = this.W_ = {};
    this.X_ = scope;

    customEvents.forEach(function (customEvent) {
      var eventType = customEvent[0];
      var targetMethodName = customEvent[1];
      var isOnce = customEvent[2];
      var extraArgs = customEvent[3];

      if (targetMethodName) {
        finalCustomEvents[eventType] = [targetMethodName, isOnce, extraArgs];
      }
    });
  },

  get el() {
    return walkFragments(this._E_);
  },

  get els() {
    // eslint-disable-next-line no-constant-condition





    return (this._E_ ? this._E_.nodes : []).filter(
      function (el) {
        return el.nodeType === ELEMENT_NODE;
      }
    );
  },

  aE_: emit,
  aF_(input, out) {
    this.onCreate && this.onCreate(input, out);
    this.aE_("create", input, out);
  },

  aG_(out) {
    this.onRender && this.onRender(out);
    this.aE_("render", out);
  },

  _y_() {
    this.onUpdate && this.onUpdate();
    this.aE_("update");
  },

  _z_() {
    this.onMount && this.onMount();
    this.aE_("mount");
  },

  ao_() {
    this.onDestroy && this.onDestroy();
    this.aE_("destroy");
  }
};

componentProto.elId = componentProto.getElId;
componentProto.aH_ = componentProto.update;
componentProto.aI_ = componentProto.destroy;

// Add all of the following DOM methods to Component.prototype:
// - appendTo(referenceEl)
// - replace(referenceEl)
// - replaceChildrenOf(referenceEl)
// - insertBefore(referenceEl)
// - insertAfter(referenceEl)
// - prependTo(referenceEl)
domInsert(
  componentProto,
  function getEl(component) {
    return component.aB_();
  },
  function afterInsert(component) {
    return component;
  }
);

inherit(Component, EventEmitter);

module.exports = Component;